import { useMutation, useQuery, useQueryClient } from "@tanstack/vue-query";
import { request } from "@/shared/http/client";

/**
 * AS PERGUNTAS PRONTAS DO PAINEL, por tela.
 *
 * A metade do Ben que a V3 não tinha. Aqui havia três frases fixas por MODO,
 * escritas em i18n — as mesmas em toda tela, iguais para um petshop e para uma
 * clínica. A V2 serve um catálogo por ROTA, escrito a partir do negócio do
 * workspace e editável à mão, e é isso que estas quatro chamadas trazem.
 *
 * ── POR QUE O CATÁLOGO INTEIRO, NUMA CONSULTA SÓ ──────────────────────────
 *
 * Porque trocar de tela não pode custar uma ida à rede: são dezenas de linhas
 * curtas, e casar a rota acontece em memória (`use-screen-suggestions.ts`). É o
 * desenho da V2 (`useBenSuggestions`, cache por sessão), com a diferença de que
 * aqui quem guarda é o `vue-query` e não um `ref` de módulo — a invalidação
 * depois de uma edição já existe de graça.
 */
export interface ScreenSuggestion {
	id: string;
	/** Um caminho do painel (`/board`), ou `*` para qualquer tela. */
	path: string;
	/** O texto curto do botão. `null` quer dizer "mostre a própria pergunta". */
	label: string | null;
	question: string;
	sortOrder: number;
	/** Quem escreveu. `regerar` só substitui o que ela mesma escreveu. */
	source: "human" | "generated";
	isActive: boolean;
}

/** O envelope é NOMEADO dos dois lados — `{ suggestions }`, nunca `{ items }`. */
interface SuggestionsResponse {
	suggestions: ScreenSuggestion[];
}

export interface GenerationResult {
	status: "generated" | "unavailable" | "empty";
	count: number;
}

const KEY = ["agent-suggestions"] as const;
const MANAGE_KEY = ["agent-suggestions", "manage"] as const;

/**
 * O catálogo que a CONVERSA desenha — só as ligadas.
 *
 * `staleTime` curto e não infinito: quem acabou de editar na tela de gestão abre
 * o Ben para ver o resultado, e a V2 chega a forçar um `reload()` no `onMounted`
 * do empty-state por causa disso. Meio minuto resolve o mesmo sem uma consulta
 * por abertura de painel.
 */
export function useScreenSuggestionCatalog() {
	return useQuery({
		queryKey: KEY,
		staleTime: 30_000,
		queryFn: () => request<SuggestionsResponse>("/v3/agents/suggestions"),
	});
}

/** A visão de gestão: com as desligadas, que a conversa nunca recebe. */
export function useManagedSuggestions() {
	return useQuery({
		queryKey: MANAGE_KEY,
		queryFn: () => request<SuggestionsResponse>("/v3/agents/suggestions/manage"),
	});
}

/** Toda escrita invalida as DUAS listas: a de gestão e a que o painel lê. */
function useAfterWrite() {
	const client = useQueryClient();
	return () => {
		void client.invalidateQueries({ queryKey: KEY });
		void client.invalidateQueries({ queryKey: MANAGE_KEY });
	};
}

export function useCreateSuggestion() {
	const done = useAfterWrite();
	return useMutation({
		mutationFn: (input: {
			path: string;
			question: string;
			label?: string | null;
			sortOrder?: number;
		}) =>
			request<{ suggestion: ScreenSuggestion }>("/v3/agents/suggestions", {
				method: "POST",
				body: input,
			}),
		onSuccess: done,
	});
}

export function useUpdateSuggestion() {
	const done = useAfterWrite();
	return useMutation({
		mutationFn: (input: {
			id: string;
			question?: string;
			label?: string | null;
			sortOrder?: number;
			isActive?: boolean;
		}) => {
			const { id, ...body } = input;
			return request<{ suggestion: ScreenSuggestion }>(
				`/v3/agents/suggestions/${encodeURIComponent(id)}`,
				{ method: "PATCH", body },
			);
		},
		onSuccess: done,
	});
}

export function useDeleteSuggestion() {
	const done = useAfterWrite();
	return useMutation({
		mutationFn: (id: string) =>
			request<void>(`/v3/agents/suggestions/${encodeURIComponent(id)}`, { method: "DELETE" }),
		onSuccess: done,
	});
}

/**
 * (Re)escrever o catálogo a partir do negócio — e as TELAS vão no pedido.
 *
 * O servidor não tem a lista de telas: `nav-items.ts` é arquivo do front, e as
 * entidades que o tenant criou no Construtor nascem em tempo de execução. A V2
 * mantinha a mesma lista escrita à mão no back e ela envelhecia sozinha. Quem
 * chama manda os destinos que já tem em mãos.
 */
export function useGenerateSuggestions() {
	const done = useAfterWrite();
	return useMutation({
		mutationFn: (screens: ReadonlyArray<{ path: string; label: string; hint?: string }>) =>
			request<GenerationResult>("/v3/agents/suggestions/generate", {
				method: "POST",
				body: { screens },
				// Síncrono por desenho (a tela espera e recarrega), e um lote de 15
				// telas leva ~30 s no modelo. Os 15 s de sempre cortavam a espera pela
				// metade: o servidor escrevia, e a tela dizia nada (18/09).
				timeoutMs: 180_000,
			}),
		onSuccess: done,
	});
}
